import { sanitizeAutoSizeBinding } from './primitiveAutoSizeBindingUtils';
import { sanitizeTextLayerFontFamily } from '../fonts/textLayerFonts';

const primitiveTypeSet = new Set(['line', 'rect', 'circle', 'roundedRect', 'parallelogram', 'image']);
const primitiveLayerKindSet = new Set(['shape', 'text', 'image']);
const primitiveTextSourceSet = new Set(['main', 'sub', 'generic']);
const primitiveColorRoleSet = new Set(['primary', 'secondary', 'textUpper', 'textLower']);
const primitiveColorModeSet = new Set(['theme', 'custom']);
const templateFamilySet = new Set(['classic-slide', 'minimal-box', 'modern-accent', 'edge-link', 'logo-badge']);
const imageCropModeSet = new Set(['original', 'square', 'circle']);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const cloneValue = (value) => (
  value && typeof value === 'object'
    ? JSON.parse(JSON.stringify(value))
    : value
);

const sanitizeNumber = (value, fallback, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return clamp(numeric, min, max);
};

const sanitizeBoolean = (value, fallback) => (
  typeof value === 'boolean' ? value : fallback
);

const sanitizeString = (value, fallback = '') => (
  typeof value === 'string' ? value : fallback
);

const sanitizeNullableString = (value, fallback = null) => {
  if (typeof value !== 'string') return fallback;
  return value.trim() ? value : fallback;
};

const sanitizePrimitiveType = (value, fallback = 'rect') => (
  primitiveTypeSet.has(value) ? value : fallback
);

const sanitizePrimitiveLayerKind = (value, fallback = 'shape') => (
  primitiveLayerKindSet.has(value) ? value : fallback
);

const sanitizePrimitiveTextSource = (value, fallback = 'main') => (
  primitiveTextSourceSet.has(value) ? value : fallback
);

const sanitizePrimitiveColorRole = (value, fallback = 'primary') => (
  primitiveColorRoleSet.has(value) ? value : fallback
);

const sanitizePrimitiveColorMode = (value, fallback = 'theme') => (
  primitiveColorModeSet.has(value) ? value : fallback
);

const getPrimitiveMinHeight = (type, layerKind) => {
  if (type === 'image') return 8;
  if (type === 'line') return 1;
  if (layerKind === 'text') return 1;
  return 8;
};

const getPrimitiveMaxCornerRadius = (type) => {
  if (type === 'roundedRect') return 540;
  if (type === 'image') return 999;
  return 0;
};

const sanitizePrimitiveResetSnapshot = (snapshot, fallbackPrimitive) => {
  const fallbackType = sanitizePrimitiveType(fallbackPrimitive?.type, 'rect');
  const fallbackLayerKind = sanitizePrimitiveLayerKind(
    fallbackPrimitive?.layerKind,
    fallbackType === 'image' ? 'image' : 'shape',
  );
  const type = sanitizePrimitiveType(snapshot?.type, fallbackType);
  const layerKind = sanitizePrimitiveLayerKind(
    snapshot?.layerKind,
    fallbackLayerKind,
  );
  const maxCornerRadius = getPrimitiveMaxCornerRadius(type);
  const minHeight = getPrimitiveMinHeight(type, layerKind);
  const base = {
    type,
    layerKind,
    x: sanitizeNumber(snapshot?.x, Number(fallbackPrimitive?.x) || 60, -1920, 1920),
    y: sanitizeNumber(snapshot?.y, Number(fallbackPrimitive?.y) || 40, -1080, 1080),
    width: sanitizeNumber(snapshot?.width, Number(fallbackPrimitive?.width) || 220, 8, 1920),
    height: sanitizeNumber(snapshot?.height, Number(fallbackPrimitive?.height) || 80, minHeight, 1080),
    rotation: sanitizeNumber(snapshot?.rotation, Number(fallbackPrimitive?.rotation) || 0, -180, 180),
    strokeWidth: sanitizeNumber(snapshot?.strokeWidth, Number(fallbackPrimitive?.strokeWidth) || 3, 1, 80),
    cornerRadius: sanitizeNumber(
      snapshot?.cornerRadius,
      Number(fallbackPrimitive?.cornerRadius) || 0,
      0,
      maxCornerRadius,
    ),
    skewX: type === 'parallelogram'
      ? sanitizeNumber(snapshot?.skewX, Number(fallbackPrimitive?.skewX) || 0, -1920, 1920)
      : 0,
    fill: sanitizeBoolean(snapshot?.fill, fallbackPrimitive?.fill !== false),
    stroke: sanitizeBoolean(snapshot?.stroke, Boolean(fallbackPrimitive?.stroke)),
    colorRole: sanitizePrimitiveColorRole(snapshot?.colorRole, sanitizePrimitiveColorRole(fallbackPrimitive?.colorRole, 'primary')),
    fillColorMode: sanitizePrimitiveColorMode(snapshot?.fillColorMode, sanitizePrimitiveColorMode(fallbackPrimitive?.fillColorMode, 'theme')),
    fillColorRole: sanitizePrimitiveColorRole(snapshot?.fillColorRole, sanitizePrimitiveColorRole(fallbackPrimitive?.fillColorRole, sanitizePrimitiveColorRole(fallbackPrimitive?.colorRole, 'primary'))),
    fillColorValue: sanitizeString(snapshot?.fillColorValue, sanitizeString(fallbackPrimitive?.fillColorValue, '#ffffff')),
    strokeColorMode: sanitizePrimitiveColorMode(snapshot?.strokeColorMode, sanitizePrimitiveColorMode(fallbackPrimitive?.strokeColorMode, 'theme')),
    strokeColorRole: sanitizePrimitiveColorRole(snapshot?.strokeColorRole, sanitizePrimitiveColorRole(fallbackPrimitive?.strokeColorRole, sanitizePrimitiveColorRole(fallbackPrimitive?.colorRole, 'primary'))),
    strokeColorValue: sanitizeString(snapshot?.strokeColorValue, sanitizeString(fallbackPrimitive?.strokeColorValue, '#ffffff')),
    mainTextVisible: sanitizeBoolean(snapshot?.mainTextVisible, Boolean(fallbackPrimitive?.mainTextVisible)),
    subTextVisible: sanitizeBoolean(snapshot?.subTextVisible, Boolean(fallbackPrimitive?.subTextVisible)),
    textSource: sanitizePrimitiveTextSource(snapshot?.textSource, sanitizePrimitiveTextSource(fallbackPrimitive?.textSource, 'main')),
    textPaddingX: sanitizeNumber(snapshot?.textPaddingX, Number(fallbackPrimitive?.textPaddingX) || 0, 0, 1920),
    textFontRatio: sanitizeNumber(snapshot?.textFontRatio, Number(fallbackPrimitive?.textFontRatio) || (fallbackLayerKind === 'text' && fallbackPrimitive?.textSource === 'main' ? 0.8 : 0.7), 0.2, 1.5),
    textBaselineRatio: sanitizeNumber(snapshot?.textBaselineRatio, Number(fallbackPrimitive?.textBaselineRatio) || 0.78, 0.2, 1.2),
    textFontFamily: layerKind === 'text'
      ? sanitizeTextLayerFontFamily(snapshot?.textFontFamily ?? fallbackPrimitive?.textFontFamily)
      : null,
    textValue: sanitizeString(snapshot?.textValue, sanitizeString(fallbackPrimitive?.textValue, '')),
    hideFromPeoplePanel: sanitizeBoolean(snapshot?.hideFromPeoplePanel, Boolean(fallbackPrimitive?.hideFromPeoplePanel)),
    imageData: typeof snapshot?.imageData === 'string'
      ? snapshot.imageData
      : (typeof fallbackPrimitive?.imageData === 'string' ? fallbackPrimitive.imageData : null),
    imageCropMode: imageCropModeSet.has(snapshot?.imageCropMode)
      ? snapshot.imageCropMode
      : (imageCropModeSet.has(fallbackPrimitive?.imageCropMode) ? fallbackPrimitive.imageCropMode : 'original'),
    autoSizeBinding: sanitizeAutoSizeBinding(snapshot?.autoSizeBinding ?? fallbackPrimitive?.autoSizeBinding, {
      type,
      layerKind,
    }),
    templateFamily: templateFamilySet.has(snapshot?.templateFamily)
      ? snapshot.templateFamily
      : (templateFamilySet.has(fallbackPrimitive?.templateFamily) ? fallbackPrimitive.templateFamily : null),
    templatePart: sanitizeNullableString(snapshot?.templatePart, sanitizeNullableString(fallbackPrimitive?.templatePart, null)),
  };

  if (layerKind === 'text') {
    base.fill = false;
    base.stroke = false;
    base.cornerRadius = 0;
  }
  if (type === 'image' || layerKind === 'image') {
    base.layerKind = 'image';
    base.fill = false;
    base.stroke = false;
    base.cornerRadius = sanitizeNumber(base.cornerRadius, base.imageCropMode === 'circle' ? 999 : 0, 0, 999);
  }
  if (type === 'circle') {
    const size = sanitizeNumber(snapshot?.width ?? snapshot?.height, base.width, 8, 1200);
    base.width = size;
    base.height = size;
    base.cornerRadius = 0;
  }
  if (type === 'line') {
    base.fill = false;
    base.stroke = layerKind === 'text' ? false : true;
    base.cornerRadius = 0;
    base.height = sanitizeNumber(snapshot?.height, base.height, 1, 200);
  }
  return base;
};

const applyLegacyResetFieldsToSnapshot = (snapshot, primitive) => ({
  ...snapshot,
  x: Number.isFinite(Number(primitive?.resetX)) ? Number(primitive.resetX) : snapshot.x,
  y: Number.isFinite(Number(primitive?.resetY)) ? Number(primitive.resetY) : snapshot.y,
  width: Number.isFinite(Number(primitive?.resetWidth)) ? Number(primitive.resetWidth) : snapshot.width,
  height: Number.isFinite(Number(primitive?.resetHeight)) ? Number(primitive.resetHeight) : snapshot.height,
  rotation: Number.isFinite(Number(primitive?.resetRotation)) ? Number(primitive.resetRotation) : snapshot.rotation,
  strokeWidth: Number.isFinite(Number(primitive?.resetStrokeWidth)) ? Number(primitive.resetStrokeWidth) : snapshot.strokeWidth,
  cornerRadius: Number.isFinite(Number(primitive?.resetCornerRadius)) ? Number(primitive.resetCornerRadius) : snapshot.cornerRadius,
  skewX: Number.isFinite(Number(primitive?.resetSkewX)) ? Number(primitive.resetSkewX) : snapshot.skewX,
});

export const buildPrimitiveResetSnapshot = (primitive) => sanitizePrimitiveResetSnapshot({
  type: primitive?.type,
  layerKind: primitive?.layerKind,
  x: primitive?.x,
  y: primitive?.y,
  width: primitive?.width,
  height: primitive?.height,
  rotation: primitive?.rotation,
  strokeWidth: primitive?.strokeWidth,
  cornerRadius: primitive?.cornerRadius,
  skewX: primitive?.skewX,
  fill: primitive?.fill,
  stroke: primitive?.stroke,
  colorRole: primitive?.colorRole,
  fillColorMode: primitive?.fillColorMode,
  fillColorRole: primitive?.fillColorRole,
  fillColorValue: primitive?.fillColorValue,
  strokeColorMode: primitive?.strokeColorMode,
  strokeColorRole: primitive?.strokeColorRole,
  strokeColorValue: primitive?.strokeColorValue,
  mainTextVisible: primitive?.mainTextVisible,
  subTextVisible: primitive?.subTextVisible,
  textSource: primitive?.textSource,
  textPaddingX: primitive?.textPaddingX,
  textFontRatio: primitive?.textFontRatio,
  textBaselineRatio: primitive?.textBaselineRatio,
  textFontFamily: primitive?.textFontFamily,
  textValue: primitive?.textValue,
  hideFromPeoplePanel: primitive?.hideFromPeoplePanel,
  imageData: primitive?.imageData,
  imageCropMode: primitive?.imageCropMode,
  autoSizeBinding: cloneValue(primitive?.autoSizeBinding) || null,
  templateFamily: primitive?.templateFamily,
  templatePart: primitive?.templatePart,
}, primitive);

export const deriveLegacyResetFieldsFromSnapshot = (snapshot) => ({
  resetX: snapshot?.x,
  resetY: snapshot?.y,
  resetWidth: snapshot?.width,
  resetHeight: snapshot?.height,
  resetRotation: snapshot?.rotation,
  resetStrokeWidth: snapshot?.strokeWidth,
  resetCornerRadius: snapshot?.cornerRadius,
  resetSkewX: snapshot?.skewX,
});

export const normalizePrimitiveWithResetSnapshot = (primitive) => {
  if (!primitive || typeof primitive !== 'object') return primitive;
  const baseSnapshot = buildPrimitiveResetSnapshot(primitive);
  const snapshot = primitive.resetSnapshot && typeof primitive.resetSnapshot === 'object'
    ? sanitizePrimitiveResetSnapshot(primitive.resetSnapshot, primitive)
    : applyLegacyResetFieldsToSnapshot(baseSnapshot, primitive);
  const normalizedSnapshot = sanitizePrimitiveResetSnapshot(snapshot, primitive);
  return {
    ...primitive,
    resetSnapshot: normalizedSnapshot,
    ...deriveLegacyResetFieldsFromSnapshot(normalizedSnapshot),
  };
};

export const stampPrimitiveResetSnapshot = (primitive, { overwrite = false } = {}) => {
  if (!primitive || typeof primitive !== 'object') return primitive;
  if (!overwrite && primitive.resetSnapshot && typeof primitive.resetSnapshot === 'object') {
    return normalizePrimitiveWithResetSnapshot(primitive);
  }
  const resetSnapshot = buildPrimitiveResetSnapshot(primitive);
  return {
    ...primitive,
    resetSnapshot,
    ...deriveLegacyResetFieldsFromSnapshot(resetSnapshot),
  };
};

export const applyPrimitiveResetSnapshot = (
  primitive,
  snapshot,
  { preserveLayerName = true, preserveVisibility = true } = {},
) => {
  if (!primitive || typeof primitive !== 'object') return primitive;
  const normalizedPrimitive = normalizePrimitiveWithResetSnapshot(primitive);
  const resetSnapshot = sanitizePrimitiveResetSnapshot(
    snapshot && typeof snapshot === 'object' ? snapshot : normalizedPrimitive.resetSnapshot,
    normalizedPrimitive,
  );
  const restored = {
    ...normalizedPrimitive,
    ...cloneValue(resetSnapshot),
    id: normalizedPrimitive.id,
    resetSnapshot,
    ...deriveLegacyResetFieldsFromSnapshot(resetSnapshot),
  };
  if (preserveLayerName) restored.layerName = normalizedPrimitive.layerName ?? null;
  if (preserveVisibility) restored.visible = typeof normalizedPrimitive.visible === 'boolean'
    ? normalizedPrimitive.visible
    : true;
  return restored;
};

export const normalizeShapePrimitivesWithResetSnapshots = (shapePrimitives) => (
  Array.isArray(shapePrimitives)
    ? shapePrimitives.map((primitive) => normalizePrimitiveWithResetSnapshot(primitive))
    : []
);

export const normalizePresetWithResetSnapshots = (preset) => {
  if (!preset || typeof preset !== 'object') return preset;
  return {
    ...preset,
    shapePrimitives: normalizeShapePrimitivesWithResetSnapshots(preset.shapePrimitives),
  };
};
